/**
 * KVS Samagam Analytics API (Legacy)
 * 
 * Uses apiClient (axios) which has 401 interceptor.
 * Raw API endpoints also available as RTK Query in: store/api/kvsSamagamApi.ts
 * 
 * Pure utility functions (aggregation, distribution, comparison) are kept here
 * since they don't make API calls — they process data.
 */

import { apiGet } from '../../../shared/services/apiClient';

// Interface for school analytics data
export interface SchoolAnalyticsData {
  schoolID: number;
  schoolName: string;
  isPMShree: number;
  stateName?: string;
  districtName?: string;
  roName?: string;
  ownershipType?: number;
  locationType?: number;
  sponsoredBy?: number;
  stationType?: number;
  buildingType?: number;
  internetPlan?: number;
  internetType?: number;
  latitude?: number;
  longitude?: number;
  [key: string]: any;
}

// Interface for aggregated analytics
export interface AnalyticsAggregation {
  category: string;
  pmshriCount: number;
  nonPmshriCount: number;
  total: number;
  pmshriPercentage: number;
}

// Interface for infrastructure comparison
export interface InfrastructureComparison {
  facilityName: string;
  pmshriAvg: number;
  nonPmshriAvg: number;
  pmshriTotal: number;
  nonPmshriTotal: number;
}

// Fetch all schools with analytics data
export async function getAllSchoolsForAnalytics(): Promise<SchoolAnalyticsData[]> {
  try {
    const data = await apiGet('/kvis/kvprofile/rostoalldistrictstoallkvs');
    if (data && data.success && Array.isArray(data.data)) {
      const schools: SchoolAnalyticsData[] = [];
      data.data.forEach((region: any) => {
        region.districts?.forEach((district: any) => {
          district.kvsDetails?.forEach((school: any) => {
            schools.push({
              schoolID: school.schoolID || school.id || school.kvID,
              schoolName: school.schoolName || '',
              isPMShree: Number(school.isPMShree) || 0,
              stateName: school.stateName || region.state || '',
              districtName: school.districtName || district.districtName || '',
              roName: school.roName || region['RO name'] || region.roName || '',
              ownershipType: school.ownershipType,
              locationType: school.locationType,
              sponsoredBy: school.sponsoredBy,
              stationType: school.stationType,
              buildingType: school.buildingType,
              internetPlan: school.internetPlan,
              internetType: school.internetType,
              latitude: school.latitude ? Number(school.latitude) : undefined,
              longitude: school.longitude ? Number(school.longitude) : undefined,
              ...school,
            });
          });
        });
      });
      return schools;
    }
    return [];
  } catch (error) {
    console.error('Error fetching schools for analytics:', error);
    return [];
  }
}

// Fetch state-wise school data with counts
export async function getStateWiseAnalytics(): Promise<any[]> {
  try {
    const data = await apiGet('/kvis/statewisekvisdetails');
    if (data && data.success && data.data && Array.isArray(data.data.data)) {
      return data.data.data.map((state: any) => {
        const schools = state.kvisDetails || [];
        const pmshriSchools = schools.filter((s: any) => Number(s.isPMShree) > 0);
        const nonPmshriSchools = schools.filter((s: any) => Number(s.isPMShree) === 0);
        
        return {
          stateID: state.stateDetails?.stateID,
          stateName: state.stateDetails?.stateName,
          totalSchools: schools.length,
          pmshriCount: pmshriSchools.length,
          nonPmshriCount: nonPmshriSchools.length,
          pmshriPercentage: schools.length > 0 
            ? ((pmshriSchools.length / schools.length) * 100).toFixed(1) 
            : 0,
          schools,
        };
      });
    }
    return [];
  } catch (error) {
    console.error('Error fetching state-wise analytics:', error);
    return [];
  }
}

// Fetch region-wise analytics
export async function getRegionWiseAnalytics(): Promise<any[]> {
  try {
    const data = await apiGet('/kvis/kvprofile/rostoalldistrictstoallkvs');
    if (data && data.success && Array.isArray(data.data)) {
      return data.data.map((region: any) => {
        const schools: any[] = [];
        region.districts?.forEach((district: any) => {
          district.kvsDetails?.forEach((school: any) => {
            schools.push(school);
          });
        });
        
        const pmshriSchools = schools.filter((s: any) => Number(s.isPMShree) > 0);
        const nonPmshriSchools = schools.filter((s: any) => Number(s.isPMShree) === 0);
        
        return {
          roID: region.roID,
          roName: region['RO name'] || region.roName,
          totalSchools: schools.length,
          pmshriCount: pmshriSchools.length,
          nonPmshriCount: nonPmshriSchools.length,
          pmshriPercentage: schools.length > 0 
            ? ((pmshriSchools.length / schools.length) * 100).toFixed(1) 
            : 0,
          districts: region.districts?.length || 0,
        };
      }).filter((r: any) => r.roName !== 'Abroad KV');
    }
    return [];
  } catch (error) {
    console.error('Error fetching region-wise analytics:', error);
    return [];
  }
}

// Fetch infrastructure details for comparison
export async function getInfrastructureAnalytics(): Promise<any> {
  try {
    // Fetch KPI data for infrastructure category
    const infraData = await apiGet('/kvis/kvprofile/kpicatdatabyprofile?category=schoolProfile&kpi=buildingType');
    const internetData = await apiGet('/kvis/kvprofile/kpicatdatabyprofile?category=schoolProfile&kpi=internetPlan');
    
    return {
      infrastructure: infraData?.data || [],
      internet: internetData?.data || [],
    };
  } catch (error) {
    console.error('Error fetching infrastructure analytics:', error);
    return { infrastructure: [], internet: [] };
  }
}

// Aggregate schools by a specific field
export function aggregateSchoolsByField(
  schools: SchoolAnalyticsData[],
  field: string,
  labelMap?: Record<string, string>
): AnalyticsAggregation[] {
  const aggregation = new Map<string, { pmshri: number; nonPmshri: number }>();
  
  schools.forEach((school) => {
    const value = school[field];
    const key = value !== undefined && value !== null ? String(value) : 'Unknown';
    const label = labelMap?.[key] || key;
    
    if (!aggregation.has(label)) {
      aggregation.set(label, { pmshri: 0, nonPmshri: 0 });
    }
    
    const current = aggregation.get(label)!;
    if (Number(school.isPMShree) > 0) {
      current.pmshri += 1;
    } else {
      current.nonPmshri += 1;
    }
  });
  
  return Array.from(aggregation.entries())
    .map(([category, counts]) => ({
      category,
      pmshriCount: counts.pmshri,
      nonPmshriCount: counts.nonPmshri,
      total: counts.pmshri + counts.nonPmshri,
      pmshriPercentage: counts.pmshri + counts.nonPmshri > 0
        ? (counts.pmshri / (counts.pmshri + counts.nonPmshri)) * 100
        : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

// Calculate PMSHRI distribution by phase
export function getPmshriPhaseDistribution(schools: SchoolAnalyticsData[]): Array<{ phase: string; count: number; percentage: number }> {
  const phaseMap: Record<number, string> = {
    0: 'Non PM-SHRI',
    1: 'Phase 1',
    2: 'Phase 2',
    3: 'Phase 3',
    5: 'Phase 5',
    7: 'Phase 7',
  };
  
  const distribution = new Map<number, number>();
  
  schools.forEach((school) => {
    const phase = Number(school.isPMShree) || 0;
    distribution.set(phase, (distribution.get(phase) || 0) + 1);
  });
  
  const total = schools.length;
  
  return Array.from(distribution.entries())
    .map(([phase, count]) => ({
      phase: phaseMap[phase] || `Phase ${phase}`,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
    }))
    .sort((a, b) => {
      // Sort Non PM-SHRI first, then by phase number
      if (a.phase === 'Non PM-SHRI') return -1;
      if (b.phase === 'Non PM-SHRI') return 1;
      return a.phase.localeCompare(b.phase);
    });
}

// Get location type distribution
export function getLocationTypeDistribution(schools: SchoolAnalyticsData[]): AnalyticsAggregation[] {
  const locationMap: Record<string, string> = {
    '1': 'Urban',
    '2': 'Rural',
    '3': 'Abroad',
  };
  
  return aggregateSchoolsByField(schools, 'locationType', locationMap);
}

// Get sponsorship distribution
export function getSponsorshipDistribution(schools: SchoolAnalyticsData[]): AnalyticsAggregation[] {
  const sponsorMap: Record<string, string> = {
    '0': 'Foreign',
    '1': 'Civil',
    '2': 'Defence',
    '3': 'IHL',
    '4': 'Project',
  };
  
  return aggregateSchoolsByField(schools, 'sponsoredBy', sponsorMap);
}

// Get station type distribution
export function getStationTypeDistribution(schools: SchoolAnalyticsData[]): AnalyticsAggregation[] {
  const stationMap: Record<string, string> = {
    '1': 'Normal',
    '2': 'Hard',
    '3': 'North East',
    '4': 'Abroad',
  };
  
  return aggregateSchoolsByField(schools, 'stationType', stationMap);
}

// Get internet connectivity distribution
export function getInternetDistribution(schools: SchoolAnalyticsData[]): AnalyticsAggregation[] {
  const internetMap: Record<string, string> = {
    '0': 'No Internet',
    '1': 'Broadband',
    '2': 'Lease Line',
    '3': 'Optical Fiber',
    '4': 'V-SAT',
    '5': 'Others',
    '6': 'Air Fiber',
  };
  
  return aggregateSchoolsByField(schools, 'internetType', internetMap);
}

// Calculate summary statistics
export function calculateSummaryStats(schools: SchoolAnalyticsData[]) {
  const totalSchools = schools.length;
  const pmshriSchools = schools.filter(s => Number(s.isPMShree) > 0);
  const nonPmshriSchools = schools.filter(s => Number(s.isPMShree) === 0);
  
  const uniqueStates = new Set(schools.map(s => s.stateName).filter(Boolean));
  const uniqueRegions = new Set(schools.map(s => s.roName).filter(Boolean));
  const uniqueDistricts = new Set(schools.map(s => s.districtName).filter(Boolean));
  
  return {
    totalSchools,
    pmshriCount: pmshriSchools.length,
    nonPmshriCount: nonPmshriSchools.length,
    pmshriPercentage: totalSchools > 0 ? ((pmshriSchools.length / totalSchools) * 100).toFixed(1) : '0',
    totalStates: uniqueStates.size,
    totalRegions: uniqueRegions.size,
    totalDistricts: uniqueDistricts.size,
  };
}

// =====================================================
// DETAILED ANALYTICS - Fetch school full details for comparison
// =====================================================

// Interface for detailed school analytics
export interface SchoolDetailedAnalytics {
  schoolID: number;
  schoolName: string;
  isPMShree: number;
  stateName?: string;
  roName?: string;
  // Infrastructure counts
  totalClassrooms: number;
  functionalClassrooms: number;
  totalLabs: number;
  functionalLabs: number;
  hasLibrary: boolean;
  hasMultiPurposeHall: boolean;
  hasPrincipalRoom: boolean;
  // Enrollment
  totalEnrollment: number;
  totalSections: number;
  // ICT
  totalComputers: number;
  computerLabs: number;
  eClassroomTypeA: number;
  eClassroomTypeB: number;
  interactivePanels: number;
  smartTVs: number;
  hasInternet: boolean;
  // Library
  totalBooks: number;
  libraryAutomation: boolean;
  libraryInternet: boolean;
  // CWSN
  cwsnStudents: number;
  specialEducators: number;
  hasRamp: boolean;
  cwsnToilets: number;
  // Sports
  totalSportsFacilities: number;
  // Green Building
  hasRainwaterHarvesting: boolean;
  solarPowerKW: number;
  ledLights: number;
  // Safety
  hasCCTV: boolean;
  totalCCTVCameras: number;
  hasBuildingSafetyCert: boolean;
  hasFireSafetyCert: boolean;
  // Labs availability
  hasATLLab: boolean;
  hasARVRLab: boolean;
  hasRoboticsLab: boolean;
  hasDigitalLanguageLab: boolean;
  hasNCC: boolean;
}

// Fetch detailed analytics for multiple schools (batch API calls)
export async function getDetailedSchoolAnalytics(schoolIDs: number[]): Promise<SchoolDetailedAnalytics[]> {
  const results: SchoolDetailedAnalytics[] = [];
  
  // Batch process to avoid too many concurrent requests
  const batchSize = 10;
  for (let i = 0; i < schoolIDs.length; i += batchSize) {
    const batch = schoolIDs.slice(i, i + batchSize);
    const batchPromises = batch.map(async (schoolID) => {
      try {
        const data = await apiGet(`/kvis/schoolsfulldetail/${schoolID}`);
        if (data && data.success && data.data) {
          return parseSchoolDetailedData(data.data);
        }
        return null;
      } catch (error) {
        console.error(`Error fetching details for school ${schoolID}:`, error);
        return null;
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.filter((r): r is SchoolDetailedAnalytics => r !== null));
  }
  
  return results;
}

// Parse full school details into analytics format
function parseSchoolDetailedData(data: any): SchoolDetailedAnalytics | null {
  if (!data) return null;
  
  const schoolProfile = data.schoolProfile || {};
  const infrastructure = data.infrastructureDetails?.infrastructure || [];
  const enrollment = data.academicDetails?.sectionEnrolment || [];
  const ictProfile = data.facilityProfiles?.ictProfile?.[0] || {};
  const libraryProfile = data.facilityProfiles?.libraryProfile?.[0] || {};
  const cwsnProfile = data.facilityProfiles?.cwsnProfile?.[0] || {};
  const cwsnInfra = data.facilityProfiles?.cwsnInfraProfile?.[0] || {};
  const sportsInfra = data.facilityProfiles?.sportsProfile || [];
  const greenProfile = data.facilityProfiles?.greenBuildingProfile?.[0] || {};
  const cctvProfile = data.facilityProfiles?.cctvProfile?.[0] || {};
  const miscProfile = data.facilityProfiles?.miscProfile?.[0] || {};
  const atlProfile = data.facilityProfiles?.atlProfile?.[0] || {};
  const arvrProfile = data.facilityProfiles?.arvrProfile?.[0] || {};
  const roboticsProfile = data.facilityProfiles?.roboticsProfile?.[0] || {};
  const languageLabProfile = data.facilityProfiles?.languageLabProfile?.[0] || {};
  const nccProfile = data.facilityProfiles?.nccProfile?.[0] || {};
  
  // Calculate infrastructure totals
  const classrooms = infrastructure.find((i: any) => i.infraItem === 'Class Rooms') || {};
  const labs = ['Physics Lab', 'Chemistry Lab', 'Biology Lab', 'Jr. Sci. Lab', 'Maths Lab', 'Computer Lab 1', 'Computer Lab 2', 'Computer Lab 3'];
  const totalLabs = labs.reduce((sum, labName) => {
    const lab = infrastructure.find((i: any) => i.infraItem === labName);
    return sum + (lab?.totalQuantity || 0);
  }, 0);
  const functionalLabs = labs.reduce((sum, labName) => {
    const lab = infrastructure.find((i: any) => i.infraItem === labName);
    return sum + (lab?.functionalQuantity || 0);
  }, 0);
  
  const library = infrastructure.find((i: any) => i.infraItem === 'Library') || {};
  const multiPurposeHall = infrastructure.find((i: any) => i.infraItem === 'Multi Purpose Hall') || {};
  const principalRoom = infrastructure.find((i: any) => i.infraItem === 'Principal Room') || {};
  
  // Calculate enrollment totals
  const totalEnrollment = enrollment.reduce((sum: number, e: any) => sum + (e.currentEnrolment || 0), 0);
  const totalSections = enrollment.reduce((sum: number, e: any) => sum + (e.sanctionedSections || 0), 0);
  
  // Calculate sports facilities with quantity > 0
  const totalSportsFacilities = sportsInfra.filter((s: any) => s.quantity > 0).length;
  
  // Calculate total books in library
  const totalBooks = (libraryProfile.hindiTextBooks || 0) + (libraryProfile.englishTextBooks || 0) +
    (libraryProfile.hindiReferenceBooks || 0) + (libraryProfile.englishReferenceBooks || 0) +
    (libraryProfile.hindiFictionBooks || 0) + (libraryProfile.englishFictionBooks || 0) +
    (libraryProfile.hindiNonFictionBooks || 0) + (libraryProfile.englishNonFictionBooks || 0);
  
  return {
    schoolID: schoolProfile.schoolID,
    schoolName: schoolProfile.schoolName || '',
    isPMShree: Number(schoolProfile.isPMShree) || 0,
    stateName: data.locationDetails?.state?.stateName || '',
    roName: data.administrativeDetails?.ro?.roName || '',
    // Infrastructure
    totalClassrooms: classrooms.totalQuantity || 0,
    functionalClassrooms: classrooms.functionalQuantity || 0,
    totalLabs,
    functionalLabs,
    hasLibrary: (library.totalQuantity || 0) > 0,
    hasMultiPurposeHall: (multiPurposeHall.totalQuantity || 0) > 0,
    hasPrincipalRoom: (principalRoom.totalQuantity || 0) > 0,
    // Enrollment
    totalEnrollment,
    totalSections,
    // ICT
    totalComputers: ictProfile.totalComputersAvailableForStudents || 0,
    computerLabs: ictProfile.noComputerLabs || 0,
    eClassroomTypeA: ictProfile.noEClassRoomTypeA || 0,
    eClassroomTypeB: ictProfile.noEClassRoomTypeB || 0,
    interactivePanels: (ictProfile.noInteractivePanelInClassRoom || 0) + (ictProfile.noInteractivePanelOtherthanClassRoom || 0),
    smartTVs: ictProfile.noSmartTV || 0,
    hasInternet: ictProfile.isKVhaveInternetConnectivity === true,
    // Library
    totalBooks,
    libraryAutomation: libraryProfile.isLibraryAutomation === true,
    libraryInternet: libraryProfile.internateAvailableInLibrary === true,
    // CWSN
    cwsnStudents: (cwsnProfile.noStudentUnderCWSNPrimary || 0) + (cwsnProfile.noStudentUnderCWSNSecondary || 0),
    specialEducators: (cwsnProfile.noSpecialEducatorPrimary || 0) + (cwsnProfile.noSpecialEducatorSecondary || 0),
    hasRamp: cwsnInfra.rampInGroundFloor === true || cwsnInfra.rampInOtherFloors === true,
    cwsnToilets: (cwsnInfra.toiletForCWSNBoys || 0) + (cwsnInfra.toiletForCWSNGirls || 0),
    // Sports
    totalSportsFacilities,
    // Green Building
    hasRainwaterHarvesting: greenProfile.rainWaterHarvestingSystem === true,
    solarPowerKW: greenProfile.solarPowerPlant || 0,
    ledLights: greenProfile.noLedLights || 0,
    // Safety
    hasCCTV: cctvProfile.isCCTVInstalled === true,
    totalCCTVCameras: cctvProfile.totalCCTVCamera || 0,
    hasBuildingSafetyCert: miscProfile.isBuildingSafetyAvailable === true,
    hasFireSafetyCert: miscProfile.isFireSafetyAvailable === true,
    // Labs
    hasATLLab: atlProfile.isATLabEstablished === true,
    hasARVRLab: arvrProfile.isARVRAvailable === true,
    hasRoboticsLab: roboticsProfile.isRoboticsAvailable === true,
    hasDigitalLanguageLab: languageLabProfile.isDigitalLanguageLabFunctional === true,
    hasNCC: nccProfile.isNCCAvailable === true,
  };
}

// =====================================================
// AGGREGATION FUNCTIONS FOR DETAILED ANALYTICS
// =====================================================

export interface DetailedComparisonStats {
  category: string;
  pmshriValue: number;
  nonPmshriValue: number;
  pmshriAvg: number;
  nonPmshriAvg: number;
  pmshriCount: number;
  nonPmshriCount: number;
}

// Calculate infrastructure comparison between PM-SHRI and Non PM-SHRI
export function calculateInfrastructureComparison(schools: SchoolDetailedAnalytics[]): DetailedComparisonStats[] {
  const pmshriSchools = schools.filter(s => s.isPMShree > 0);
  const nonPmshriSchools = schools.filter(s => s.isPMShree === 0);
  
  const metrics = [
    { key: 'totalClassrooms', label: 'Classrooms' },
    { key: 'totalLabs', label: 'Science Labs' },
    { key: 'totalComputers', label: 'Computers' },
    { key: 'computerLabs', label: 'Computer Labs' },
    { key: 'interactivePanels', label: 'Interactive Panels' },
    { key: 'smartTVs', label: 'Smart TVs' },
    { key: 'totalBooks', label: 'Library Books' },
    { key: 'totalSportsFacilities', label: 'Sports Facilities' },
    { key: 'ledLights', label: 'LED Lights' },
    { key: 'totalCCTVCameras', label: 'CCTV Cameras' },
  ];
  
  return metrics.map(metric => {
    const pmshriTotal = pmshriSchools.reduce((sum, s) => sum + ((s as any)[metric.key] || 0), 0);
    const nonPmshriTotal = nonPmshriSchools.reduce((sum, s) => sum + ((s as any)[metric.key] || 0), 0);
    
    return {
      category: metric.label,
      pmshriValue: pmshriTotal,
      nonPmshriValue: nonPmshriTotal,
      pmshriAvg: pmshriSchools.length > 0 ? pmshriTotal / pmshriSchools.length : 0,
      nonPmshriAvg: nonPmshriSchools.length > 0 ? nonPmshriTotal / nonPmshriSchools.length : 0,
      pmshriCount: pmshriSchools.length,
      nonPmshriCount: nonPmshriSchools.length,
    };
  });
}

// Calculate facility availability comparison (boolean fields)
export function calculateFacilityAvailability(schools: SchoolDetailedAnalytics[]): Array<{
  facility: string;
  pmshriCount: number;
  pmshriPercent: number;
  nonPmshriCount: number;
  nonPmshriPercent: number;
}> {
  const pmshriSchools = schools.filter(s => s.isPMShree > 0);
  const nonPmshriSchools = schools.filter(s => s.isPMShree === 0);
  
  const facilities = [
    { key: 'hasLibrary', label: 'Library' },
    { key: 'hasMultiPurposeHall', label: 'Multi-Purpose Hall' },
    { key: 'hasInternet', label: 'Internet Connectivity' },
    { key: 'libraryAutomation', label: 'Library Automation' },
    { key: 'hasRamp', label: 'CWSN Ramp' },
    { key: 'hasCCTV', label: 'CCTV System' },
    { key: 'hasRainwaterHarvesting', label: 'Rainwater Harvesting' },
    { key: 'hasBuildingSafetyCert', label: 'Building Safety Certificate' },
    { key: 'hasFireSafetyCert', label: 'Fire Safety Certificate' },
    { key: 'hasATLLab', label: 'ATL Lab' },
    { key: 'hasARVRLab', label: 'AR/VR Lab' },
    { key: 'hasRoboticsLab', label: 'Robotics/AI Lab' },
    { key: 'hasDigitalLanguageLab', label: 'Digital Language Lab' },
    { key: 'hasNCC', label: 'NCC' },
  ];
  
  return facilities.map(facility => {
    const pmshriWithFacility = pmshriSchools.filter(s => (s as any)[facility.key] === true).length;
    const nonPmshriWithFacility = nonPmshriSchools.filter(s => (s as any)[facility.key] === true).length;
    
    return {
      facility: facility.label,
      pmshriCount: pmshriWithFacility,
      pmshriPercent: pmshriSchools.length > 0 ? (pmshriWithFacility / pmshriSchools.length) * 100 : 0,
      nonPmshriCount: nonPmshriWithFacility,
      nonPmshriPercent: nonPmshriSchools.length > 0 ? (nonPmshriWithFacility / nonPmshriSchools.length) * 100 : 0,
    };
  });
}

// Calculate enrollment comparison
export function calculateEnrollmentComparison(schools: SchoolDetailedAnalytics[]): {
  pmshriTotal: number;
  nonPmshriTotal: number;
  pmshriAvg: number;
  nonPmshriAvg: number;
  pmshriSections: number;
  nonPmshriSections: number;
} {
  const pmshriSchools = schools.filter(s => s.isPMShree > 0);
  const nonPmshriSchools = schools.filter(s => s.isPMShree === 0);
  
  const pmshriEnrollment = pmshriSchools.reduce((sum, s) => sum + s.totalEnrollment, 0);
  const nonPmshriEnrollment = nonPmshriSchools.reduce((sum, s) => sum + s.totalEnrollment, 0);
  const pmshriSections = pmshriSchools.reduce((sum, s) => sum + s.totalSections, 0);
  const nonPmshriSections = nonPmshriSchools.reduce((sum, s) => sum + s.totalSections, 0);
  
  return {
    pmshriTotal: pmshriEnrollment,
    nonPmshriTotal: nonPmshriEnrollment,
    pmshriAvg: pmshriSchools.length > 0 ? pmshriEnrollment / pmshriSchools.length : 0,
    nonPmshriAvg: nonPmshriSchools.length > 0 ? nonPmshriEnrollment / nonPmshriSchools.length : 0,
    pmshriSections,
    nonPmshriSections,
  };
}

// Calculate CWSN comparison
export function calculateCWSNComparison(schools: SchoolDetailedAnalytics[]): {
  pmshriStudents: number;
  nonPmshriStudents: number;
  pmshriEducators: number;
  nonPmshriEducators: number;
  pmshriWithRamp: number;
  nonPmshriWithRamp: number;
  pmshriToilets: number;
  nonPmshriToilets: number;
} {
  const pmshriSchools = schools.filter(s => s.isPMShree > 0);
  const nonPmshriSchools = schools.filter(s => s.isPMShree === 0);
  
  return {
    pmshriStudents: pmshriSchools.reduce((sum, s) => sum + s.cwsnStudents, 0),
    nonPmshriStudents: nonPmshriSchools.reduce((sum, s) => sum + s.cwsnStudents, 0),
    pmshriEducators: pmshriSchools.reduce((sum, s) => sum + s.specialEducators, 0),
    nonPmshriEducators: nonPmshriSchools.reduce((sum, s) => sum + s.specialEducators, 0),
    pmshriWithRamp: pmshriSchools.filter(s => s.hasRamp).length,
    nonPmshriWithRamp: nonPmshriSchools.filter(s => s.hasRamp).length,
    pmshriToilets: pmshriSchools.reduce((sum, s) => sum + s.cwsnToilets, 0),
    nonPmshriToilets: nonPmshriSchools.reduce((sum, s) => sum + s.cwsnToilets, 0),
  };
}

// Calculate Green Building comparison
export function calculateGreenBuildingComparison(schools: SchoolDetailedAnalytics[]): {
  pmshriRainwater: number;
  nonPmshriRainwater: number;
  pmshriSolarKW: number;
  nonPmshriSolarKW: number;
  pmshriLedLights: number;
  nonPmshriLedLights: number;
} {
  const pmshriSchools = schools.filter(s => s.isPMShree > 0);
  const nonPmshriSchools = schools.filter(s => s.isPMShree === 0);
  
  return {
    pmshriRainwater: pmshriSchools.filter(s => s.hasRainwaterHarvesting).length,
    nonPmshriRainwater: nonPmshriSchools.filter(s => s.hasRainwaterHarvesting).length,
    pmshriSolarKW: pmshriSchools.reduce((sum, s) => sum + s.solarPowerKW, 0),
    nonPmshriSolarKW: nonPmshriSchools.reduce((sum, s) => sum + s.solarPowerKW, 0),
    pmshriLedLights: pmshriSchools.reduce((sum, s) => sum + s.ledLights, 0),
    nonPmshriLedLights: nonPmshriSchools.reduce((sum, s) => sum + s.ledLights, 0),
  };
}

// Get building type distribution
export function getBuildingTypeDistribution(schools: SchoolAnalyticsData[]): AnalyticsAggregation[] {
  const buildingMap: Record<string, string> = {
    '1': 'Permanent',
    '2': 'Temporary',
    '3': 'Semi-Permanent',
  };
  
  return aggregateSchoolsByField(schools, 'buildingType', buildingMap);
}

// Get ownership type distribution
export function getOwnershipTypeDistribution(schools: SchoolAnalyticsData[]): AnalyticsAggregation[] {
  const ownershipMap: Record<string, string> = {
    '1': 'Owned',
    '2': 'Leased',
    '3': 'Rented',
  };
  
  return aggregateSchoolsByField(schools, 'ownershipType', ownershipMap);
}
